import { useState } from 'react'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

/**
 * useWithdrawConfirm
 *
 * Shared hook that encapsulates the "are you sure you want to withdraw?"
 * confirm modal flow used by the various Document views. Returns the state
 * + handlers needed to drive a <WithdrawAllVersionsModal/>.
 *
 * Usage:
 *   const withdraw = useWithdrawConfirm({
 *       originalStatusName,
 *       statusName,
 *       formRef,
 *       saveButtonRef,
 *       handleSubmit,
 *   })
 *
 *   ...
 *   <SaveButton onClick={withdraw.handleSaveClick} ref={saveButtonRef} ... />
 *   <WithdrawAllVersionsModal entityLabel="Document" {...withdraw} />
 *
 * The boolean `withdraw.withdrawAllVersions` should be sent to the backend
 * as `withdraw_all_versions` in the save request body.
 */
export const useWithdrawConfirm = ({
    originalStatusName,
    statusName,
    formRef,
    saveButtonRef,
    handleSubmit,
    enabled = true,
}) => {
    const [show, setShow] = useState(false)
    const [withdrawAllVersions, setWithdrawAllVersions] = useState(false)

    const handleSaveClick = (...args) => {
        if (enabled && originalStatusName !== 'Withdrawn' && statusName === 'Withdrawn') {
            setWithdrawAllVersions(false)
            setShow(true)
            return null
        }
        return handleSubmit(...args)
    }

    const handleConfirmWithdraw = () => {
        const form = formRef?.current
        if (form && !form.checkValidity()) {
            form.reportValidity()
            return
        }
        setShow(false)
        const actionProps = handleSubmit()
        if (actionProps === null || actionProps === undefined) {
            return
        }
        saveButtonRef?.current?.submit(actionProps)
    }

    const handleCancel = () => setShow(false)

    return {
        show,
        withdrawAllVersions,
        setWithdrawAllVersions,
        handleSaveClick,
        handleConfirmWithdraw,
        handleCancel,
    }
}

/**
 * WithdrawAllVersionsModal
 *
 * Confirmation modal shown when a user changes a document's status to
 * "Withdrawn". Lets the user opt to withdraw ALL versions of the document
 * at the same time. Designed to be driven by the `useWithdrawConfirm` hook.
 */
const WithdrawAllVersionsModal = ({
    show,
    entityLabel = 'Document',
    disableAllVersionsCheckbox = false,
    withdrawAllVersions,
    setWithdrawAllVersions,
    handleConfirmWithdraw,
    handleCancel,
}) => {
    const checkboxId = `withdraw-all-versions-${entityLabel.replace(/\s+/g, '-').toLowerCase()}`

    return (
        <Modal show={show} onHide={handleCancel} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Withdraw {entityLabel}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                You are about to withdraw this version. It will not be deleted but will not be available for use. Withdraw can be undone.
            </Modal.Body>
            <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {!disableAllVersionsCheckbox && (
                <div style={{ marginRight: 'auto' }} onClick={(e) => e.stopPropagation()}>
                    <div className="form-check">
                        <input
                            id={checkboxId}
                            className="form-check-input"
                            type="checkbox"
                            checked={withdrawAllVersions}
                            onChange={(e) => setWithdrawAllVersions(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={checkboxId}>
                            Withdraw ALL versions of this {entityLabel}
                        </label>
                    </div>
                </div>
                )}
                <div>
                    <Button variant="light" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmWithdraw} style={{ marginLeft: '10px' }}>
                        Confirm Withdraw
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default WithdrawAllVersionsModal
